import React, { useRef, useState } from 'react';
import { useField } from 'formik';
import { IMaskInput } from 'react-imask';
import IMask from 'imask/esm/imask';
import { useTranslation } from 'react-i18next';
import Calendar from 'react-calendar';
import cx from 'classnames';
import moment from 'moment';
import { Transition } from 'react-transition-group';
import { TransitionStatus } from 'react-transition-group/Transition';

import * as Icon from 'components/Icon';
import useOnClickOutside from 'helpers/useClickOutside';
import { FIELD_DATE_DIRECTION } from 'modules/process/constants';

import Field from '../Field';

import classes from '../Field.module.scss';
import 'react-calendar/dist/Calendar.css';

interface IProps {
  name: string;
  label: string;
  placeholder?: string;
  showError?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  required?: boolean;
  readOnly?: boolean;
  direction?: FIELD_DATE_DIRECTION | null | '';

  [key: string]: any;
}

const MINUTE_STEP = 5;
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 60 / MINUTE_STEP }, (_, i) => i * MINUTE_STEP);

const pad = (n: number) => String(n).padStart(2, '0');

const getToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const calendarLimits = (direction?: FIELD_DATE_DIRECTION | null | '') =>
  direction === FIELD_DATE_DIRECTION.BEFORE
    ? { maxDate: new Date(getToday().getTime() - 86400000) }
    : direction === FIELD_DATE_DIRECTION.AFTER
      ? { minDate: new Date(getToday().getTime() + 86400000) }
      : {};

const isDateAllowed = (date: Date, direction?: FIELD_DATE_DIRECTION | null | '') => {
  if (!direction || Number.isNaN(date.getTime())) return true;
  const today = getToday();
  if (direction === FIELD_DATE_DIRECTION.BEFORE) return date < today;
  if (direction === FIELD_DATE_DIRECTION.AFTER) return date > today;
  return true;
};

const DateTimeField: React.FC<IProps> = ({ label, placeholder, showError = true, disabled, readOnly, autoFocus, required, direction, ...props }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [level, setLevel] = useState<'date' | 'hour' | 'minute'>('date');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedHour, setSelectedHour] = useState<number | null>(null);
  const ref = useRef(null);
  useOnClickOutside(ref, () => {
    setOpen(false);
    setLevel('date');
  });

  const [field, meta, helpers] = useField(props);
  const hasError = !!(showError && meta.touched && meta.error);

  const openPicker = () => {
    setLevel('date');
    setSelectedDate(null);
    setSelectedHour(null);
    setOpen(prev => !prev);
  };

  const pickDate = (date) => {
    if (!isDateAllowed(date, direction)) return;
    setSelectedDate(date);
    setLevel('hour');
  };

  const pickHour = (hour: number) => {
    setSelectedHour(hour);
    setLevel('minute');
  };

  const pickMinute = (minute: number) => {
    if (!selectedDate || selectedHour === null) return;
    const value = `${moment(selectedDate).format('DD.MM.YYYY')} ${pad(selectedHour)}:${pad(minute)}`;
    helpers.setValue(value);
    setOpen(false);
    setLevel('date');
  };

  return (
    <Field {...{ name: field.name, label, hasError, showError, error: meta.error, autoFocus, disabled, required, readOnly }} icon={
      <div className={classes.calendar}>
        <div onClick={openPicker}>
          <Icon.System.Calendar width="20" height="20" />
        </div>
        <Transition in={open} timeout={{ enter: 0, exit: 200 }} unmountOnExit mountOnEnter>
          {(state: TransitionStatus) => (
            <div style={{ ...transition[state] }} ref={ref} className={classes.calendarContainer}>
              {level === 'date' && (
                <Calendar onChange={pickDate} {...calendarLimits(direction)} className={classes.calendarBase} />
              )}
              {level === 'hour' && (
                <div className={cx(classes.calendarBase, classes.timeList)}>
                  <div className={classes.timeListHeader} onClick={() => setLevel('date')}>
                    <Icon.System.ChevronLeft /> {selectedDate ? moment(selectedDate).format('DD.MM.YYYY') : ''}
                  </div>
                  <div className={classes.timeListGrid}>
                    {HOURS.map(hour => (
                      <div key={hour} className={classes.timeListItem} onClick={() => pickHour(hour)}>
                        {pad(hour)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {level === 'minute' && (
                <div className={cx(classes.calendarBase, classes.timeList)}>
                  <div className={classes.timeListHeader} onClick={() => setLevel('hour')}>
                    <Icon.System.ChevronLeft /> {pad(selectedHour as number)}
                  </div>
                  <div className={classes.timeListGrid}>
                    {MINUTES.map(minute => (
                      <div key={minute} className={classes.timeListItem} onClick={() => pickMinute(minute)}>
                        {pad(selectedHour as number)}:{pad(minute)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Transition>
      </div>
    }>
      {({ onFocus, onBlur }) => (
        <IMaskInput
          {...{ placeholder, disabled, onFocus, readOnly }}
          id={field.name}
          name={field.name}
          value={field.value}
          type="tel"
          onBlur={(e) => {
            field.onBlur(e);
            onBlur();
          }}
          onAccept={value => helpers.setValue(value)}
          className={classes.input}
          placeholderChar=" "
          overwrite
          unmask
          lazy={false}
          autofix
          mask="d.m.Y H:i"
          blocks={{
            d: { mask: IMask.MaskedRange, placeholderChar: t('day_placeholder'), from: 1, to: 31, maxLength: 2 },
            m: { mask: IMask.MaskedRange, placeholderChar: t('month_placeholder'), from: 1, to: 12, maxLength: 2 },
            Y: { mask: IMask.MaskedRange, placeholderChar: t('year_placeholder'), from: 1500, to: 2999, maxLength: 4 },
            H: { mask: IMask.MaskedRange, placeholderChar: t('hour_placeholder'), from: 0, to: 23, maxLength: 2 },
            i: { mask: IMask.MaskedRange, placeholderChar: t('minute_placeholder'), from: 0, to: 59, maxLength: 2 }
          }}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
        />
      )}
    </Field>
  );
};

const transition = {
  entering: { opacity: 0, visibility: 'hidden' },
  entered: { opacity: 1, visibility: 'visible', transition: 'all .2s ease-in-out' },
  exited: { opacity: 0, visibility: 'hidden' }
};

export default DateTimeField;
